/*
 * Design: Swiss Precision × 和の余白
 * ソリューションアイテム10種 + セット組み合わせ提案（3/5/7）
 * 顧客が「課題タグ→アイテム→セット」を直感的に選べるUI
 * Mobile: 横スクロールカード、タブ切替、余白最適化
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { scrollToContactForm } from "@/hooks/useScrollToContact";
import {
  Mail,
  FolderSync,
  FileEdit,
  ShieldCheck,
  Bell,
  FileSpreadsheet,
  BarChart3,
  AlertCircle,
  Lock,
  Copy,
  ArrowRight,
  Zap,
  Settings,
  Expand,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

/* ─── Data ─── */

interface SolutionItem {
  id: string;
  num: string;
  icon: React.ElementType;
  name: string;
  tagline: string;
  input: string;
  action: string;
  output: string;
  exception: string;
  painTags: string[];
}

const ITEMS: SolutionItem[] = [
  {
    id: "01",
    num: "01",
    icon: Mail,
    name: "メール自動起票",
    tagline: "Gmail → 台帳へ自動転記",
    input: "Gmail（特定ラベル / 送信元 / 件名条件）",
    action: "Gemini APIで必要情報を抽出・整形",
    output: "Google Sheets 台帳に追記",
    exception: "判定不能 → Chatで確認依頼",
    painTags: ["転記が多い", "締めが遅い"],
  },
  {
    id: "02",
    num: "02",
    icon: FolderSync,
    name: "ファイル整流化",
    tagline: "Drive整頓・命名・リンク生成",
    input: "Google Drive 新規ファイル",
    action: "命名規則適用・格納先フォルダ振分",
    output: "共有リンク生成 → 管理台帳に記録",
    exception: "命名失敗 → 保留フォルダ＋通知",
    painTags: ["転記が多い", "横展開できない"],
  },
  {
    id: "03",
    num: "03",
    icon: FileEdit,
    name: "ドラフト生成",
    tagline: "返信・報告・議事録の下書き自動化",
    input: "Gmail / Docs / Sheets",
    action: "Gemini APIで文章生成",
    output: "Docs または Gmail下書き",
    exception: "送信は必ず人が確定",
    painTags: ["差戻しが多い", "伝達が漏れる"],
  },
  {
    id: "04",
    num: "04",
    icon: ShieldCheck,
    name: "自動チェック",
    tagline: "重複・不足・金額・締日の照合",
    input: "請求書 / 見積書 / 管理台帳",
    action: "照合ルール適用 → エラー検知",
    output: "差分リスト＋通知",
    exception: "不一致 → 例外キューへ",
    painTags: ["差戻しが多い", "締めが遅い"],
  },
  {
    id: "05",
    num: "05",
    icon: Bell,
    name: "即時通知＆リマインド",
    tagline: "状態変化・期限をChatで即通知",
    input: "台帳の状態変化 / 期限カラム",
    action: "条件トリガー → 担当者メンション",
    output: "Google Chat スペースへ通知",
    exception: "通知先不明 → 管理者へ",
    painTags: ["伝達が漏れる", "締めが遅い"],
  },
  {
    id: "06",
    num: "06",
    icon: FileSpreadsheet,
    name: "フォーム即席化",
    tagline: "Forms自動生成＋回答集約",
    input: "質問項目・回答形式・対象者",
    action: "Google Forms 自動作成＋共有設定",
    output: "Sheets集約＋回答通知",
    exception: "質問曖昧 → ドラフト提示",
    painTags: ["転記が多い", "伝達が漏れる"],
  },
  {
    id: "07",
    num: "07",
    icon: BarChart3,
    name: "KPI自動集計",
    tagline: "週次・月次レポート自動生成",
    input: "管理台帳の実績データ",
    action: "集計・グラフ・要約コメント生成",
    output: "Docs / Slides レポート",
    exception: "データ欠損 → 計測依頼通知",
    painTags: ["締めが遅い", "横展開できない"],
  },
  {
    id: "08",
    num: "08",
    icon: AlertCircle,
    name: "例外キュー運用",
    tagline: "AIが迷ったら人に渡す導線",
    input: "各アイテムからの判定不能案件",
    action: "保留登録 → 担当者自動割当",
    output: "例外一覧＋Chat通知",
    exception: "期限超過 → 再通知",
    painTags: ["差戻しが多い", "伝達が漏れる"],
  },
  {
    id: "09",
    num: "09",
    icon: Lock,
    name: "権限・監査ログパック",
    tagline: "実行ログ・権限の自動棚卸",
    input: "MCP実行ログ / 権限情報",
    action: "定期棚卸 → ログ出力 → 権限過多検知",
    output: "監査ログ台帳＋月次サマリー",
    exception: "権限過多 → 是正提案",
    painTags: ["横展開できない", "差戻しが多い"],
  },
  {
    id: "10",
    num: "10",
    icon: Copy,
    name: "テンプレ横展開",
    tagline: "成功業務を他部署へ複製",
    input: "既存の成功業務（設定・プロンプト）",
    action: "テンプレート化・業務レシピ作成",
    output: "他部署へ複製可能なパッケージ",
    exception: "部署差分 → カスタマイズガイド",
    painTags: ["横展開できない"],
  },
];

interface SolutionSet {
  id: string;
  name: string;
  subtitle: string;
  tagline: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
  itemIds: string[];
  effect: string;
  period: string;
}

const SETS: SolutionSet[] = [
  {
    id: "quick",
    name: "Quick Win",
    subtitle: "3アイテム",
    tagline: "現場が「回る」最低限",
    icon: Zap,
    color: "text-[#E03030]",
    bgColor: "bg-[#FEF2F2]",
    borderColor: "border-[#E03030]",
    itemIds: ["01", "05", "08"],
    effect: "転記工数 60〜70% 削減 / 通知漏れゼロ化",
    period: "2〜4週間",
  },
  {
    id: "standard",
    name: "Standard Ops",
    subtitle: "5アイテム",
    tagline: "現場が「回り続ける」運用まで",
    icon: Settings,
    color: "text-[#1A1A1A]",
    bgColor: "bg-[#F9F9F9]",
    borderColor: "border-[#1A1A1A]",
    itemIds: ["01", "05", "08", "02", "07"],
    effect: "＋ファイル検索 80% 削減 / 月次集計 90% 削減",
    period: "1〜2ヶ月",
  },
  {
    id: "scale",
    name: "Scale & Control",
    subtitle: "7アイテム",
    tagline: "会社が「増やせる」統制まで",
    icon: Expand,
    color: "text-white",
    bgColor: "bg-[#1A1A1A]",
    borderColor: "border-[#1A1A1A]",
    itemIds: ["01", "05", "08", "02", "07", "04", "09"],
    effect: "＋チェック工数 70% 削減 / 監査対応 50% 削減",
    period: "2〜3ヶ月",
  },
];

const PAIN_TAGS = [
  { label: "すべて", value: "all" },
  { label: "転記が多い", value: "転記が多い" },
  { label: "差戻しが多い", value: "差戻しが多い" },
  { label: "締めが遅い", value: "締めが遅い" },
  { label: "伝達が漏れる", value: "伝達が漏れる" },
  { label: "横展開できない", value: "横展開できない" },
];

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

/* ─── Sub-components ─── */

function ItemCard({ item, isHighlighted }: { item: SolutionItem; isHighlighted: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = item.icon;

  return (
    <motion.div
      layout
      className={`border p-4 sm:p-5 transition-all duration-300 ${
        isHighlighted
          ? "border-[#E03030] bg-[#FEF2F2]/50 shadow-[0_2px_16px_rgba(224,48,48,0.08)]"
          : "border-[#E0E0E0] bg-white hover:border-[#E03030]/40"
      }`}
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-sm ${
            isHighlighted ? "bg-[#E03030] text-white" : "bg-[#1A1A1A] text-white"
          }`}
        >
          <Icon size={18} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span
              className="text-[10px] font-bold text-[#E03030] tracking-wider"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              ITEM {item.num}
            </span>
          </div>
          <h4 className="text-sm sm:text-base font-bold text-[#1A1A1A] leading-snug">
            {item.name}
          </h4>
          <p className="text-[11px] sm:text-xs text-[#666666] mt-0.5">{item.tagline}</p>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="shrink-0 w-8 h-8 flex items-center justify-center text-[#666666] hover:text-[#E03030] transition-colors touch-manipulation"
          aria-label={expanded ? "詳細を閉じる" : "詳細を見る"}
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Pain tags */}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {item.painTags.map((tag) => (
          <span
            key={tag}
            className="text-[10px] font-medium px-2 py-0.5 bg-[#F9F9F9] border border-[#E0E0E0] text-[#666666]"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Expandable detail */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="mt-4 pt-3 border-t border-[#E0E0E0] space-y-2">
              {[
                { label: "Input", value: item.input, color: "bg-blue-500" },
                { label: "Action", value: item.action, color: "bg-amber-500" },
                { label: "Output", value: item.output, color: "bg-emerald-500" },
                { label: "Exception", value: item.exception, color: "bg-red-500" },
              ].map((row) => (
                <div key={row.label} className="flex items-start gap-2">
                  <span
                    className={`shrink-0 mt-1 text-[9px] font-bold text-white px-1.5 py-0.5 rounded-sm ${row.color}`}
                    style={{ fontFamily: "'Inter', sans-serif", minWidth: "56px", textAlign: "center" }}
                  >
                    {row.label}
                  </span>
                  <span className="text-xs text-[#333333] leading-relaxed">{row.value}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function SetCard({ set, isActive, onClick }: { set: SolutionSet; isActive: boolean; onClick: () => void }) {
  const Icon = set.icon;
  const isScale = set.id === "scale";

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 sm:p-5 border-2 transition-all duration-300 touch-manipulation ${
        isActive
          ? `${set.borderColor} ${isScale ? "bg-[#1A1A1A]" : set.bgColor} shadow-[0_4px_20px_rgba(0,0,0,0.08)]`
          : "border-[#E0E0E0] bg-white hover:border-[#999]"
      }`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div
          className={`w-8 h-8 flex items-center justify-center rounded-sm ${
            isActive && isScale
              ? "bg-[#E03030] text-white"
              : isActive
                ? "bg-[#1A1A1A] text-white"
                : "bg-[#F9F9F9] text-[#666666]"
          }`}
        >
          <Icon size={16} strokeWidth={1.5} />
        </div>
        <div>
          <span
            className={`text-[10px] font-bold tracking-wider block ${
              isActive && isScale ? "text-[#E03030]" : "text-[#666666]"
            }`}
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {set.subtitle}
          </span>
          <span
            className={`text-sm sm:text-base font-bold ${
              isActive && isScale ? "text-white" : "text-[#1A1A1A]"
            }`}
          >
            {set.name}
          </span>
        </div>
      </div>
      <p
        className={`text-xs leading-relaxed ${
          isActive && isScale ? "text-[#CCCCCC]" : "text-[#666666]"
        }`}
      >
        {set.tagline}
      </p>
    </button>
  );
}

/* ─── Main Section ─── */

export function SolutionItemsSection() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeSet, setActiveSet] = useState<string>("quick");

  const filteredItems =
    activeFilter === "all"
      ? ITEMS
      : ITEMS.filter((item) => item.painTags.includes(activeFilter));

  const currentSet = SETS.find((s) => s.id === activeSet)!;
  const setItems = currentSet.itemIds.map((id) => ITEMS.find((item) => item.id === id)!);

  return (
    <section id="solutions" className="py-14 sm:py-20 lg:py-28 bg-[#F9F9F9]">
      <div className="container">
        {/* ── Section Header ── */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="mb-8 sm:mb-14">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-1.5 sm:w-2 h-7 sm:h-8 bg-[#E03030]" />
            <span
              className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Solution Items & Sets
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#1A1A1A] mb-3 sm:mb-4">
            課題から選ぶ、組み合わせ提案
          </h2>
          <p className="text-sm sm:text-base text-[#333333] max-w-3xl leading-relaxed">
            現場の「困りごと」を5つの課題タグに分類し、それぞれに対応するソリューションアイテムをご用意しています。
            3 / 5 / 7 のセットから、お客様の状況に合った組み合わせをお選びいただけます。
          </p>
        </motion.div>

        {/* ── Part 1: アイテム一覧（課題タグフィルタ付き） ── */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.1 }} className="mb-12 sm:mb-20">
          <h3 className="text-base sm:text-lg font-bold text-[#1A1A1A] mb-4 sm:mb-6">
            ソリューションアイテム一覧
          </h3>

          {/* Pain Tag Filter */}
          <div className="flex gap-2 overflow-x-auto pb-3 mb-5 sm:mb-6 scrollbar-hide -mx-1 px-1">
            {PAIN_TAGS.map((tag) => (
              <button
                key={tag.value}
                onClick={() => setActiveFilter(tag.value)}
                className={`shrink-0 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border transition-all duration-200 touch-manipulation ${
                  activeFilter === tag.value
                    ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                    : "bg-white text-[#333333] border-[#E0E0E0] hover:border-[#999]"
                }`}
              >
                {tag.label}
              </button>
            ))}
          </div>

          {/* Item Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <AnimatePresence mode="popLayout">
              {filteredItems.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                >
                  <ItemCard
                    item={item}
                    isHighlighted={currentSet.itemIds.includes(item.id)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* ── Part 2: セット組み合わせ提案 ── */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6, delay: 0.2 }}>
          <h3 className="text-base sm:text-lg font-bold text-[#1A1A1A] mb-2">
            セット組み合わせ提案
          </h3>
          <p className="text-xs sm:text-sm text-[#666666] mb-5 sm:mb-8 leading-relaxed">
            Quick Win から始めて、効果を確認しながらステップアップ。積み上げ式だから、無駄なく拡張できます。
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-8">
            {/* Left: Set selector tabs */}
            <div className="lg:col-span-4 flex flex-row lg:flex-col gap-3">
              {SETS.map((set) => (
                <SetCard
                  key={set.id}
                  set={set}
                  isActive={activeSet === set.id}
                  onClick={() => setActiveSet(set.id)}
                />
              ))}
            </div>

            {/* Right: Selected set detail */}
            <div className="lg:col-span-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSet}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white border border-[#E0E0E0] p-5 sm:p-7 lg:p-8"
                >
                  {/* Set header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6 pb-4 sm:pb-5 border-b border-[#E0E0E0]">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="text-[10px] font-bold tracking-wider text-[#E03030]"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {currentSet.subtitle.toUpperCase()}
                        </span>
                      </div>
                      <h4 className="text-lg sm:text-xl font-bold text-[#1A1A1A]">
                        {currentSet.name}
                        <span className="text-sm sm:text-base font-normal text-[#666666] ml-2">
                          セット
                        </span>
                      </h4>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] sm:text-xs text-[#666666] block">実装期間</span>
                      <span className="text-sm sm:text-base font-bold text-[#1A1A1A]">
                        {currentSet.period}
                      </span>
                    </div>
                  </div>

                  {/* Included items */}
                  <div className="space-y-3 mb-5 sm:mb-6">
                    {setItems.map((item, i) => {
                      const Icon = item.icon;
                      const isNew = currentSet.id === "standard" && (item.id === "02" || item.id === "07")
                        || currentSet.id === "scale" && (item.id === "04" || item.id === "09");

                      return (
                        <div
                          key={item.id}
                          className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 border transition-colors ${
                            isNew
                              ? "border-[#E03030]/30 bg-[#FEF2F2]/50"
                              : "border-[#E0E0E0] bg-[#F9F9F9]"
                          }`}
                        >
                          <div className="shrink-0 w-8 h-8 flex items-center justify-center bg-[#1A1A1A] text-white rounded-sm">
                            <Icon size={14} strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className="text-[9px] font-bold text-[#E03030] tracking-wider"
                                style={{ fontFamily: "'Inter', sans-serif" }}
                              >
                                {item.num}
                              </span>
                              <span className="text-xs sm:text-sm font-bold text-[#1A1A1A]">
                                {item.name}
                              </span>
                              {isNew && (
                                <span className="text-[9px] font-bold text-[#E03030] bg-[#FEF2F2] border border-[#E03030]/20 px-1.5 py-0.5">
                                  NEW
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] sm:text-xs text-[#666666] mt-0.5 truncate">
                              {item.tagline}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Effect summary */}
                  <div className="bg-[#F9F9F9] border border-[#E0E0E0] p-4 sm:p-5 mb-5 sm:mb-6">
                    <span className="text-[10px] font-bold tracking-wider text-[#666666] block mb-1.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                      EXPECTED EFFECT
                    </span>
                    <p className="text-xs sm:text-sm font-medium text-[#1A1A1A]">
                      {currentSet.effect}
                    </p>
                    <p className="text-[10px] text-[#666666] mt-2">
                      ※ 削減率は仮置き値です。PoC期間中に実測値へ置き換えます。
                    </p>
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => scrollToContactForm({ set: currentSet.id, items: currentSet.itemIds })}
                    className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 bg-[#E03030] text-white text-sm font-bold hover:bg-[#c72828] transition-colors active:scale-[0.98] touch-manipulation"
                  >
                    このセットについて相談する
                    <ArrowRight size={14} />
                  </button>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
